/**
 * Browser Order Cancellation Bot
 * Monitors and cancels unfilled orders after timeout
 * Matches Electron implementation exactly
 */
class BrowserCancellationBot {
    constructor() {
        this.enabled = false;
        this.trackedOrders = new Map();
        this.monitorInterval = null;
        this.cancelAfterHours = 1;
        this.checkIntervalMs = 2000; // Check every 2 seconds
        this.userAddress = null;
        
        // console.log('🤖 [CANCELLATION-BOT] Initializing Browser Cancellation Bot...');
        
        // Load existing data from LocalStorage on startup
        this.loadData();
        
        // console.log(`🤖 [CANCELLATION-BOT] Loaded state - Enabled: ${this.enabled}, Hours: ${this.cancelAfterHours}, Tracked: ${this.trackedOrders.size}`);
    }

    /**
     * Enable the bot and start monitoring
     * @returns {boolean} Success status
     */
    enable() {
        if (this.enabled) {
            // console.log('🤖 [CANCELLATION-BOT] Already enabled, skipping');
            return true;
        }
        
        // console.log('🤖 [CANCELLATION-BOT] 🟢 ENABLING BOT');
        this.enabled = true;
        this.saveSettings();
        this.startMonitoring();
        
        return true;
    }

    /**
     * Disable the bot and stop monitoring
     * @returns {boolean} Success status
     */
    disable() {
        if (!this.enabled) {
            // console.log('🤖 [CANCELLATION-BOT] Already disabled, skipping');
            return true;
        }
        
        // console.log('🤖 [CANCELLATION-BOT] 🔴 DISABLING BOT');
        this.enabled = false;
        this.saveSettings();
        this.stopMonitoring();
        
        return true;
    }

    /**
     * Check if bot is currently enabled
     * @returns {boolean} Enabled status
     */
    isEnabled() {
        return this.enabled;
    }

    /**
     * Set the user address for order queries
     * @param {string} address - User wallet address
     */
    setUserAddress(address) {
        // console.log(`🤖 [CANCELLATION-BOT] Setting user address: ${address}`);
        this.userAddress = address;
    }

    /**
     * Track a new order for cancellation monitoring
     * Only tracks entry orders (non-reduce-only) since canceling entry auto-cancels TP/SL
     * @param {Object} orderData - Order details from successful placement
     */
    trackOrder(orderData) {
        if (!this.enabled) {
            // console.log(`🤖 [CANCELLATION-BOT] Bot disabled, not tracking order`);
            return;
        }

        // Only track entry orders (non-reduce-only)
        if (orderData.reduceOnly) {
            // console.log(`🤖 [CANCELLATION-BOT] Skipping reduce-only order (TP/SL)`);
            return;
        }

        // Use OID as primary, fallback to CLOID (matches terminal-view pattern)
        const orderId = String(orderData.oid || orderData.cloid);

        const trackingData = {
            orderId: orderId,
            oid: orderData.oid,
            cloid: orderData.cloid,
            asset: orderData.asset,
            direction: orderData.direction,
            entryPrice: orderData.entryPrice,
            targetPrice: orderData.targetPrice,
            stopPrice: orderData.stopPrice,
            size: orderData.size || '0.01',
            placedAt: Date.now(),
            isEntryOrder: true
        };

        this.trackedOrders.set(orderId, trackingData);
        this.saveData();

        // console.log(`🤖 [CANCELLATION-BOT] ✅ Tracking order ${orderId} for ${orderData.asset}`);
        
        // Send notification about order being tracked
        this.sendNotification('order-tracked', trackingData);
    }

    /**
     * Start the monitoring interval
     */
    startMonitoring() {
        if (this.monitorInterval) {
            // console.log('🤖 [CANCELLATION-BOT] Monitoring already running');
            return;
        }

        // console.log(`🤖 [CANCELLATION-BOT] 🔄 Starting monitoring loop (every ${this.checkIntervalMs / 1000}s)`);
        
        // Run immediately on start
        this.checkOrders();
        
        this.monitorInterval = setInterval(() => {
            this.checkOrders();
        }, this.checkIntervalMs);
    }

    /**
     * Stop the monitoring interval
     */
    stopMonitoring() {
        if (this.monitorInterval) {
            clearInterval(this.monitorInterval);
            this.monitorInterval = null;
            // console.log('🤖 [CANCELLATION-BOT] ⏸️ Monitoring stopped');
        }
    }

    /**
     * Check all open orders and cancel old entry orders
     * CRITICAL: Matches Electron flow exactly - checks enabled FIRST, then hours settings, then orders
     */
    async checkOrders() {
        // console.log('\n🤖 [CANCELLATION-BOT] ═══════════════════════════════════════════');
        // console.log('🤖 [CANCELLATION-BOT] 🔍 STARTING ORDER CHECK CYCLE');
        // console.log('🤖 [CANCELLATION-BOT] ═══════════════════════════════════════════');
        
        // STEP 1: Check if bot is enabled (USER REQUIREMENT - CHECK FIRST)
        if (!this.enabled) {
            // console.log('🤖 [CANCELLATION-BOT] ⏸️ Bot is DISABLED, skipping check');
            return;
        }
        // console.log('🤖 [CANCELLATION-BOT] ✅ Bot is ENABLED, proceeding...');
        
        // STEP 2: Check if client is available
        if (!window.BrowserHyperliquidClient || !window.BrowserHyperliquidClient.isInitialized) {
            // console.log('🤖 [CANCELLATION-BOT] ⚠️ BrowserHyperliquidClient not initialized, skipping check');
            return;
        }
        // console.log('🤖 [CANCELLATION-BOT] ✅ Hyperliquid client available');
        
        // STEP 3: Log current settings (USER REQUIREMENT - CHECK HOURS SETTING)
        // console.log(`🤖 [CANCELLATION-BOT] ⚙️ Current Settings:`);
        // console.log(`   • Cancel After: ${this.cancelAfterHours} hours`);
        // console.log(`   • Check Interval: ${this.checkIntervalMs / 1000} seconds`);
        // console.log(`   • Tracked Orders: ${this.trackedOrders.size}`);

        try {
            // STEP 4: Get user's current open orders from Hyperliquid
            // console.log('🤖 [CANCELLATION-BOT] 📡 Fetching open orders from API...');
            const openOrders = await this.getUserOpenOrders();
            // console.log(`🤖 [CANCELLATION-BOT] 📊 Retrieved ${openOrders.length} total open orders`);
            
            // STEP 5: Filter to entry orders only (non-reduce-only, non-TP/SL)
            const entryOrders = openOrders.filter(order => !order.reduceOnly && !order.isPositionTpsl);
            // console.log(`🤖 [CANCELLATION-BOT] 🎯 Filtered to ${entryOrders.length} entry orders (non-reduce-only)`);
            
            // STEP 6: Update tracked orders with current entry orders
            this.updateTrackedOrders(entryOrders);
            // console.log(`🤖 [CANCELLATION-BOT] 📝 Tracking ${this.trackedOrders.size} orders after update`);
            
            // STEP 7: Check each tracked order against CURRENT cancelAfterHours setting
            const now = Date.now();
            const ordersToCancel = [];
            
            // console.log('\n🤖 [CANCELLATION-BOT] 🔎 Checking tracked orders for cancellation...');

            for (const [orderId, orderData] of this.trackedOrders) {
                // Use original timestamp for age calculation
                const orderTime = new Date(orderData.originalTimestamp || orderData.placedAt).getTime();
                const ageMs = now - orderTime;
                const hoursElapsed = ageMs / (1000 * 60 * 60);

                // console.log(`🤖 [CANCELLATION-BOT]   📌 Order ${orderId}:`);
                // console.log(`      Asset: ${orderData.asset}, Side: ${orderData.direction}, Size: ${orderData.size}`);
                // console.log(`      Age: ${hoursElapsed.toFixed(2)}h, Threshold: ${this.cancelAfterHours}h`);

                // STEP 8: Cancel if age >= cancelAfterHours (USES CURRENT SETTING)
                if (hoursElapsed >= this.cancelAfterHours) {
                    // console.log(`      ⚠️ ORDER EXCEEDS THRESHOLD - WILL CANCEL`);
                    // console.log(`🤖 [CANCELLATION-BOT] 🗑️ Attempting to cancel order ${orderId} (${hoursElapsed.toFixed(1)}h >= ${this.cancelAfterHours}h)`);
                    
                    const cancelled = await this.cancelOrder(orderId, orderData);
                    // console.log(`🤖 [CANCELLATION-BOT] Cancel result for ${orderId}: ${cancelled ? '✅ SUCCESS' : '❌ FAILED'}`);
                    
                    if (cancelled) {
                        ordersToCancel.push(orderId);
                        
                        // Send cancellation notification
                        this.sendNotification('order-cancelled', {
                            asset: orderData.asset,
                            direction: orderData.direction,
                            size: orderData.size,
                            price: orderData.entryPrice,
                            age: hoursElapsed.toFixed(1),
                            orderId: orderData.orderId
                        });
                        
                        // Log to console
                        // console.log(`🗑️ Bot cancelled: ${orderData.asset} ${orderData.direction} ${orderData.size} @ $${orderData.entryPrice} (${hoursElapsed.toFixed(1)}h old)`);
                    }
                } else {
                    const remainingHours = (this.cancelAfterHours - hoursElapsed).toFixed(2);
                    // console.log(`      ✅ Order still valid - ${remainingHours}h remaining until cancellation`);
                }
            }

            // STEP 9: Remove cancelled orders from tracking
            if (ordersToCancel.length > 0) {
                // console.log(`\n🤖 [CANCELLATION-BOT] 🧹 Removing ${ordersToCancel.length} cancelled orders from tracking...`);
                ordersToCancel.forEach(orderId => {
                    this.trackedOrders.delete(orderId);
                    // console.log(`   • Removed ${orderId}`);
                });
                this.saveData();
            } else {
                // console.log(`\n🤖 [CANCELLATION-BOT] ✅ No orders cancelled this cycle`);
            }
            
            // console.log('\n🤖 [CANCELLATION-BOT] ═══════════════════════════════════════════');
            // console.log('🤖 [CANCELLATION-BOT] ✅ ORDER CHECK CYCLE COMPLETE');
            // console.log('🤖 [CANCELLATION-BOT] ═══════════════════════════════════════════\n');

        } catch (error) {
            // console.error('🤖 [CANCELLATION-BOT] ❌ Error during order check:', error);
            // console.error('   Error details:', error.message);
            // console.error('   Stack:', error.stack);
        }
    }

    /**
     * Update tracked orders with current entry orders from API
     * @param {Array} entryOrders - Current entry orders from open orders
     */
    updateTrackedOrders(entryOrders) {
        const now = Date.now();
        
        // Add new entry orders to tracking
        entryOrders.forEach(order => {
            // Use OID as primary, fallback to CLOID (matches terminal-view pattern)
            const orderId = String(order.oid || order.cloid);
            
            if (!this.trackedOrders.has(orderId)) {
                const trackingData = {
                    orderId: orderId,
                    oid: order.oid,
                    cloid: order.cloid,
                    asset: order.coin,
                    direction: order.side,
                    entryPrice: parseFloat(order.limitPx || order.triggerPx || 0),
                    size: order.sz,
                    placedAt: now,
                    originalTimestamp: order.timestamp, // Store original API timestamp
                    isEntryOrder: true
                };
                
                this.trackedOrders.set(orderId, trackingData);
                // console.log(`   • Added new order to tracking: ${orderId} (${order.coin})`);
            }
        });
        
        // Remove orders that are no longer open
        const openOrderIds = new Set(entryOrders.map(order => String(order.oid || order.cloid)));
        for (const orderId of this.trackedOrders.keys()) {
            if (!openOrderIds.has(orderId)) {
                this.trackedOrders.delete(orderId);
                // console.log(`   • Removed filled/cancelled order from tracking: ${orderId}`);
            }
        }
        
        this.saveData();
    }

    /**
     * Get user's open orders from Hyperliquid API
     * @returns {Array} Array of open orders
     */
    async getUserOpenOrders() {
        try {
            const openOrders = await window.BrowserHyperliquidClient.getUserOpenOrders();
            
            if (!openOrders || !Array.isArray(openOrders)) {
                // console.warn('🤖 [CANCELLATION-BOT] ⚠️ No orders returned from API');
                return [];
            }
            
            // Transform to match expected format
            return openOrders.map(order => ({
                coin: order.coin,
                side: order.side === 'A' ? 'SELL' : 'BUY',
                sz: order.sz,
                limitPx: order.limitPx,
                triggerPx: order.triggerPx,
                oid: order.oid,
                cloid: order.cloid || null,
                isPositionTpsl: order.isPositionTpsl || false,
                reduceOnly: order.reduceOnly || false,
                timestamp: order.timestamp || Date.now()
            }));
            
        } catch (error) {
            // console.error('🤖 [CANCELLATION-BOT] ❌ Error fetching open orders:', error);
            return [];
        }
    }

    /**
     * Cancel an order by order ID (OID or CLOID)
     * @param {string} orderId - Order ID (OID or CLOID)
     * @param {Object} orderData - Order tracking data
     * @returns {boolean} Success status
     */
    async cancelOrder(orderId, orderData) {
        try {
            // console.log(`🤖 [CANCELLATION-BOT] 🔄 Calling BrowserHyperliquidClient.cancelOrder for ${orderId}...`);
            
            // Use BrowserHyperliquidClient's cancelOrder method which auto-detects OID vs CLOID
            const result = await window.BrowserHyperliquidClient.cancelOrder({
                orderId: orderId,
                asset: orderData.asset
            });

            if (result.success) {
                // console.log(`🤖 [CANCELLATION-BOT] ✅ Successfully cancelled order ${orderId}`);
                return true;
            } else {
                // console.warn(`🤖 [CANCELLATION-BOT] ⚠️ Failed to cancel order ${orderId}:`, result.error || result.message);
                return false;
            }
        } catch (error) {
            // console.error(`🤖 [CANCELLATION-BOT] ❌ Error cancelling order ${orderId}:`, error);
            return false;
        }
    }

    /**
     * Send notification via CustomEvent to renderer process
     * @param {string} type - Notification type
     * @param {Object} data - Order data
     */
    sendNotification(type, data) {
        // console.log(`🤖 [CANCELLATION-BOT] 📢 Dispatching event - Type: ${type}`, data);
        
        // Dispatch CustomEvent for UI
        window.dispatchEvent(new CustomEvent('cancellation-bot-notification', {
            detail: { type, data }
        }));
        
        // console.log(`🤖 [CANCELLATION-BOT] ✅ Event dispatched`);
        
        // Also send to NotificationManager if available
        if (window.NotificationManager) {
            if (type === 'order-cancelled') {
                window.NotificationManager.showBotNotification('bot-cancelled-order', {
                    asset: data.asset,
                    message: `Cancelled ${data.asset} ${data.direction} ${data.size} @ $${data.price} (${data.age}h old)`
                });
            }
        }
    }

    /**
     * Get current bot status and statistics
     * @returns {Object} Status information
     */
    getStatus() {
        const status = {
            enabled: this.enabled,
            trackedOrdersCount: this.trackedOrders.size,
            cancelAfterHours: this.cancelAfterHours,
            checkIntervalMs: this.checkIntervalMs,
            trackedOrders: Array.from(this.trackedOrders.entries()).map(([cloid, data]) => {
                const now = Date.now();
                const orderTime = new Date(data.originalTimestamp || data.placedAt).getTime();
                const ageMs = now - orderTime;
                const hoursElapsed = ageMs / (1000 * 60 * 60);
                
                return {
                    cloid,
                    ...data,
                    hoursElapsed: hoursElapsed
                };
            })
        };
        
        // console.log(`🤖 [CANCELLATION-BOT] 📊 Status requested:`, status);
        return status;
    }

    /**
     * Update bot settings
     * @param {Object} settings - New settings
     */
    updateSettings(settings) {
        // console.log(`🤖 [CANCELLATION-BOT] ⚙️ Updating settings:`, settings);
        
        if (settings.cancelAfterHours !== undefined) {
            const oldValue = this.cancelAfterHours;
            this.cancelAfterHours = Math.max(0.1, Math.min(9999, settings.cancelAfterHours));
            // console.log(`   • Cancel After Hours: ${oldValue}h → ${this.cancelAfterHours}h`);
        }
        
        this.saveSettings();
        
        // Send notification
        this.sendNotification('settings-updated', {
            cancelAfterHours: this.cancelAfterHours
        });
        
        // console.log(`🤖 [CANCELLATION-BOT] ✅ Settings updated and saved`);
    }

    /**
     * Save bot settings to LocalStorage
     */
    saveSettings() {
        const data = this.loadDataFromStorage();
        data.settings = {
            enabled: this.enabled,
            cancelAfterHours: this.cancelAfterHours
        };
        localStorage.setItem('cancellation-bot-data', JSON.stringify(data));
        // console.log(`🤖 [CANCELLATION-BOT] 💾 Settings saved to LocalStorage`);
    }

    /**
     * Save tracked orders to LocalStorage
     */
    saveData() {
        const data = this.loadDataFromStorage();
        data.orders = Object.fromEntries(this.trackedOrders);
        localStorage.setItem('cancellation-bot-data', JSON.stringify(data));
        // console.log(`🤖 [CANCELLATION-BOT] 💾 Tracked orders saved to LocalStorage (${this.trackedOrders.size} orders)`);
    }

    /**
     * Load data from LocalStorage
     */
    loadData() {
        const data = this.loadDataFromStorage();
        
        // Load settings
        if (data.settings) {
            this.enabled = data.settings.enabled || false;
            this.cancelAfterHours = data.settings.cancelAfterHours || 1;
            // console.log(`🤖 [CANCELLATION-BOT] 📂 Loaded settings - Enabled: ${this.enabled}, Hours: ${this.cancelAfterHours}`);
        }

        // Load tracked orders
        if (data.orders) {
            this.trackedOrders = new Map(Object.entries(data.orders));
            // console.log(`🤖 [CANCELLATION-BOT] 📂 Loaded ${this.trackedOrders.size} tracked orders`);
        }

        // Start monitoring if enabled
        if (this.enabled) {
            // console.log(`🤖 [CANCELLATION-BOT] 🟢 Bot was enabled in saved state, starting monitoring...`);
            this.startMonitoring();
        }
    }

    /**
     * Load data from LocalStorage
     * @returns {Object} Data object
     */
    loadDataFromStorage() {
        try {
            const stored = localStorage.getItem('cancellation-bot-data');
            if (stored) {
                return JSON.parse(stored);
            }
        } catch (error) {
            // console.error('🤖 [CANCELLATION-BOT] ❌ Error loading data from LocalStorage:', error);
        }

        return {
            settings: { enabled: false, cancelAfterHours: 1 },
            orders: {}
        };
    }
}

// Create singleton instance
if (typeof window !== 'undefined') {
    window.BrowserCancellationBot = new BrowserCancellationBot();
    // console.log('✅ BrowserCancellationBot singleton initialized on window');
}

export { BrowserCancellationBot };
